import {Injectable} from "@angular/core";
import {LoginUseCase} from "@app/core/user/application/login.use-case";
import {PopupNotifier} from "@app/presentation/components/errors/popup.notifier";
import {Credentials} from "@app/core/credentials/domain/credentials";
import {take} from "rxjs/operators";
import {createStatusStore} from "@app/presentation/rxjs-utils/create-status-store";
import {onIsLoadingChanges} from "@app/presentation/rxjs-utils/operators/on-is-loading-changes";
import {DeliveryPointSelector} from "@app/core/delivery-point/application/select-delivery-point.use-case";
import {onError} from "@app/presentation/rxjs-utils/operators/on-error";
import {User} from "@app/core/user/domain/entities/user";
import {firstValueFrom} from 'rxjs';
import {LogoutUseCase} from "@app/core/user/application/logout.use-case";
import {LoginWithEncryptedPasswordUseCase} from "@app/core/user/application/login-with-encrypted-password.use-case";
import {RecaptchaAction, RecaptchaService} from "@app/presentation/services/recaptcha/recaptcha.service";

@Injectable()
export class LoginViewModel {

  private loginStatusStore = createStatusStore();
  readonly isLoginLoading$ = this.loginStatusStore.isLoading$

  public notifier = new PopupNotifier();

  constructor(
      private loginUseCase: LoginUseCase,
      private logoutUseCase: LogoutUseCase,
      private loginWithEncryptedPasswordUseCase: LoginWithEncryptedPasswordUseCase,
      private recaptchaService: RecaptchaService,
  ) { }

  public async login(credentials: Credentials, deliveryPointSelector: DeliveryPointSelector, promoter: string): Promise<User | null> {
    const token = await firstValueFrom(this.recaptchaService.getToken(RecaptchaAction.LOGIN));
    return firstValueFrom(this.loginUseCase.execute({credentials, deliveryPointSelector, token, promoter}).pipe(
      take(1),
      onIsLoadingChanges(isLoading => this.loginStatusStore.setIsLoading(isLoading)),
      onError(error => this.notifier.notifyError(error)),
    ))
  }

  public async loginWithEncryptedPassword(credentials: Credentials, deliveryPointSelector: DeliveryPointSelector, promoter: string): Promise<User | null> {
    const recaptchaToken = await firstValueFrom(this.recaptchaService.getToken(RecaptchaAction.LOGIN));
    return firstValueFrom(this.loginWithEncryptedPasswordUseCase.execute({credentials, deliveryPointSelector, recaptchaToken, promoter}).pipe(
      take(1),
      onIsLoadingChanges(isLoading => this.loginStatusStore.setIsLoading(isLoading)),
      onError(error => this.notifier.notifyError(error)),
    ))
  }

  public logOut(): Promise<void> {
    return firstValueFrom(this.logoutUseCase.execute())
  }
}

